import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Button, Input, Space, Typography } from 'antd';
// Api
import ENV from 'api/env';
import { GET_AFFILIATE_STREAMS } from 'api/streamAffiliate/queries';
// Types
import {
  AffiliateStreamsOrderBy,
  SortDirection,
} from 'api/graphql-global-types';
import {
  GetAffiliateStreams,
  GetAffiliateStreamsVariables,
  GetAffiliateStreams_getAffiliateStreams_entities,
} from 'api/streamAffiliate/types/GetAffiliateStreams';
// Helpers
import { getEnvLink } from 'helpers/getEnvLink';
import { formatCurrencyString, getPublicStreamDate } from 'helpers/utils';
// UI
import Table, {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_NUMBER_ITEMS_PER_PAGE,
  SortedInfo,
} from 'ui/Table';
// Components
import AffiliateStreamLinksModal from './AffiliateStreamLinksModal/AffiliateStreamLinksModal';
// Styles
import styles from './AffiliateStreamsTable.module.scss';

type AffiliateStreamRow = GetAffiliateStreams_getAffiliateStreams_entities;

// Sortable columns are keyed by the API's order-by value, which is how ui/Table matches
// a column to the server-side sort state.
type AffiliateStreamItem = {
  id: string;
  streamName: string;
  streamUrl: string | null;
  organizer: string;
  price: number;
  commissionPercent: number | null;
  linksCount: number;
  ticketsSoldCount: number;
  [AffiliateStreamsOrderBy.ScheduleDate]: string;
  [AffiliateStreamsOrderBy.ActiveLinks]: number;
  [AffiliateStreamsOrderBy.Clicks]: number;
  [AffiliateStreamsOrderBy.Sales]: number;
  [AffiliateStreamsOrderBy.Revenue]: number;
  [AffiliateStreamsOrderBy.Commission]: number;
};

const { Text } = Typography;

const getOrganizerName = (
  store: AffiliateStreamRow['stream']['store']
): string =>
  store.storeDetails?.storeName ||
  `${store.firstName || ''} ${store.lastName || ''}`.trim() ||
  store.slug ||
  '';

// A store without a slug has no public page, so its stream has no public URL either.
const getStreamUrl = (stream: AffiliateStreamRow['stream']): string | null =>
  stream.store.slug
    ? `${getEnvLink(ENV.REACT_APP_ENV)}/${stream.store.slug}/streams/${
        stream.slug
      }`
    : null;

const toItems = (entities: AffiliateStreamRow[]): AffiliateStreamItem[] =>
  entities.map(({ stream, ...totals }) => ({
    id: stream.id,
    streamName: stream.name || 'Untitled stream',
    streamUrl: getStreamUrl(stream),
    organizer: getOrganizerName(stream.store),
    price: stream.price,
    commissionPercent: stream.affiliateCommissionPercent,
    linksCount: totals.linksCount,
    ticketsSoldCount: totals.ticketsSoldCount,
    [AffiliateStreamsOrderBy.ScheduleDate]: getPublicStreamDate(
      stream.scheduleDateUtc
    ),
    [AffiliateStreamsOrderBy.ActiveLinks]: totals.activeLinksCount,
    [AffiliateStreamsOrderBy.Clicks]: totals.clicks,
    [AffiliateStreamsOrderBy.Sales]: totals.salesCount,
    [AffiliateStreamsOrderBy.Revenue]: totals.revenue,
    [AffiliateStreamsOrderBy.Commission]: totals.commissionTotal,
  }));

/** Every stream whose affiliate program is on and still has a link that can attribute sales, with its links' totals. */
const AffiliateStreamsTable = (): JSX.Element => {
  const [search, setSearch] = useState('');
  const [selectedStream, setSelectedStream] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_CURRENT_PAGE);
  const [pageSize, setPageSize] = useState<number>(
    DEFAULT_NUMBER_ITEMS_PER_PAGE
  );
  const [sort, setSortInfo] = useState<SortedInfo<AffiliateStreamsOrderBy>>({
    order: SortDirection.DESC,
    key: AffiliateStreamsOrderBy.ScheduleDate,
  });

  const { data, loading } = useQuery<
    GetAffiliateStreams,
    GetAffiliateStreamsVariables
  >(GET_AFFILIATE_STREAMS, {
    variables: {
      input: {
        search: search || null,
        orderBy: sort.key,
        direction: sort.order,
        offset: (currentPage - 1) * pageSize,
        limit: pageSize,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const handleSearch = (value: string) => {
    setSearch(value.trim());
    setCurrentPage(DEFAULT_CURRENT_PAGE);
  };

  const columns = [
    {
      title: 'Stream',
      dataIndex: 'streamName',
      key: 'streamName',
      fixed: 'left' as const,
      width: 240,
      render: function StreamName(name: string, item: AffiliateStreamItem) {
        return item.streamUrl ? (
          <a href={item.streamUrl} target="_blank" rel="noopener noreferrer">
            {name}
          </a>
        ) : (
          name
        );
      },
    },
    {
      title: 'Organizer',
      dataIndex: 'organizer',
      key: 'organizer',
      width: 180,
    },
    {
      title: 'Date',
      dataIndex: AffiliateStreamsOrderBy.ScheduleDate,
      key: AffiliateStreamsOrderBy.ScheduleDate,
      sorterType: 'date',
      width: 180,
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      align: 'right' as const,
      width: 100,
      render: (price: number) => formatCurrencyString(price),
    },
    {
      title: 'Rate',
      dataIndex: 'commissionPercent',
      key: 'commissionPercent',
      align: 'center' as const,
      width: 80,
      render: (percent: number | null) =>
        percent === null ? '—' : `${percent}%`,
    },
    {
      title: 'Active links',
      dataIndex: AffiliateStreamsOrderBy.ActiveLinks,
      key: AffiliateStreamsOrderBy.ActiveLinks,
      sorterType: 'number',
      align: 'center' as const,
      width: 120,
      render: function ActiveLinks(active: number, item: AffiliateStreamItem) {
        return (
          <Button
            type="link"
            onClick={() =>
              setSelectedStream({ id: item.id, name: item.streamName })
            }
          >
            {active} / {item.linksCount}
          </Button>
        );
      },
    },
    {
      title: 'Clicks',
      dataIndex: AffiliateStreamsOrderBy.Clicks,
      key: AffiliateStreamsOrderBy.Clicks,
      sorterType: 'number',
      align: 'center' as const,
      width: 100,
    },
    {
      title: 'PPV sales',
      dataIndex: AffiliateStreamsOrderBy.Sales,
      key: AffiliateStreamsOrderBy.Sales,
      sorterType: 'number',
      align: 'center' as const,
      width: 110,
    },
    {
      title: 'Tickets',
      dataIndex: 'ticketsSoldCount',
      key: 'ticketsSoldCount',
      align: 'center' as const,
      width: 90,
    },
    {
      title: 'Affiliate revenue',
      dataIndex: AffiliateStreamsOrderBy.Revenue,
      key: AffiliateStreamsOrderBy.Revenue,
      sorterType: 'number',
      align: 'right' as const,
      width: 130,
      render: (revenue: number) => formatCurrencyString(revenue),
    },
    {
      title: 'Commission',
      dataIndex: AffiliateStreamsOrderBy.Commission,
      key: AffiliateStreamsOrderBy.Commission,
      sorterType: 'number',
      align: 'right' as const,
      width: 130,
      render: (commission: number) => formatCurrencyString(commission),
    },
  ];

  return (
    <>
      <Space
        direction="horizontal"
        align="baseline"
        className={styles.spaceWrapper}
      >
        <Input.Search
          placeholder="Search by stream name"
          allowClear
          onSearch={handleSearch}
          className={styles.search}
        />
        <Text type="secondary">
          Streams with the affiliate program on and at least one active link.
          Affiliate revenue is the organizer net from sales through affiliate
          links.
        </Text>
      </Space>

      <Table<AffiliateStreamItem, AffiliateStreamsOrderBy>
        columns={columns}
        data={toItems(data?.getAffiliateStreams?.entities || [])}
        rowKey={(item) => item.id}
        scroll={{ x: 1400 }}
        loading={loading}
        total={data?.getAffiliateStreams?.total}
        currentPage={currentPage}
        pageSize={pageSize}
        setCurrentPage={setCurrentPage}
        setPageSize={setPageSize}
        sortInfo={sort}
        setSortInfo={setSortInfo}
      />

      {/* Keyed by stream so each stream opens with a fresh status filter and page. */}
      <AffiliateStreamLinksModal
        key={selectedStream?.id}
        stream={selectedStream}
        onClose={() => setSelectedStream(null)}
      />
    </>
  );
};

export default AffiliateStreamsTable;
